import { Component, OnInit, AfterViewInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CustomDropdownComponent } from '../../custom-dropdown/custom-dropdown.component';
import { RightSideDrawerService } from '../right-side-drawer.service';
import { SessionService } from '../../session-service/session.service';
import { DefineProjectsService } from '../../../data-analysis-summary/define-projects/define-projects.service';
import { AssignSurveyRespondentService } from '../../right-side-drawer/assign-survey-respondent/assign-survey-respondent.service';
import { EpAddProjectService } from './ep-add-projects.service';
import { ActivityTypeEnum } from 'src/app/shared/constants/activity-type.enum';
import { SelectedDeletedProjectActivity } from '../../models/edit-survey/getsurvey-projects-activity.model';
import { CustomDropDown } from '../../models/custom-dropdown.model';
import { BaseResponse } from '../../models/base-response.model';
import { Project } from '../../models/project.model';
import { Messages } from '../../constants/messages';
import { SESSION_CONST } from 'src/app/shared/constants/session-constants'
import { APP_CONSTANTS } from '../../constants/app-constants';
import { ToastrService } from '@kpmg-tax/ui';
import { delay, map, switchMap, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { encodeText } from 'src/app/shared/lib/custom-tools';
import { ActivatedRoute } from '@angular/router';
import { LegalEntitiesService } from 'src/app/data-analysis-summary/legal-entity/legal-entity.service';
import { IDropdownSettings } from 'ng-multiselect-dropdown';

@Component({
  selector: 'ep-add-projects',
  templateUrl: './ep-add-projects.component.html',
  styleUrls: ['./ep-add-projects.component.scss']
})

export class EpAddProjectsComponent implements OnInit, AfterViewInit, OnDestroy {
  private _subscriptions: Subscription[] = [];
  @ViewChild(CustomDropdownComponent) private customDropdownComponent!: CustomDropdownComponent;
  @Input() drawerTitle: string;
  @Input() calledFromEditSurvey?: boolean;
  targetForm: FormGroup;
  project: Project;
  isEdit: boolean;
  existingProjectNumbers: string[];
  existingProjectNames: string[];
  projectNoError: boolean = false;
  projectNameDuplicateError: boolean = false;
  isAddProjectButtonDisabled: boolean = true;
  isProjectName: boolean = false;
  engagementId: string;
  dropdownWidth = '';
  addProjectSelectText = '';
  dropdownOptions: CustomDropDown[] = [];
  selectedProjectType = '';
  isProjectTypeEmpty = false;
  showProjectTypeValidation = false;
  isProjAddingFromSurvey = false;
  projectEntityAssociationOptions = [
    { id: 1, value: 'All entities (include any added in future)', isSelected: false },
    { id: 2, value: 'Specific entities', isSelected: false }
  ]
  legalEntities: { legalEntityId: number, legalEntityName: string, isSelected: boolean }[] = [];
  entityName: { legalEntityId: number, legalEntityName: string }[] = [];
  noSearchResults = false;
  dropdownSettings: IDropdownSettings;
  isSpecificEntitySelected = false;
  isProjectEntityAssociated = false;

  constructor(private route: ActivatedRoute, private legalEntityService: LegalEntitiesService,
    public _assignSurveyRepondentService: AssignSurveyRespondentService,
    public _rightSideDrawerService: RightSideDrawerService,
    public _sessionService: SessionService,
    public _projectsService: DefineProjectsService,
    public _formBuilder: FormBuilder,
    public _toastrService: ToastrService,
    public _epAddProjectService: EpAddProjectService) {
    this.dropdownOptions = this.getProjectOptions();
    this.engagementId = this._sessionService.getSession('engagementId');
  }

  // ================================== getters and setters START =======================================

  get f() {
    return this.targetForm?.controls;
  }

  get inputProjectNumber(): string {
    return this.targetForm.get('projectNumber').value?.toString().trim();
  }

  get inputProjectName(): string {
    return this.targetForm.get('name').value?.toString().trim();
  }

  get inputProjectDescription(): string {
    return this.targetForm.get('desc').value?.toString().trim();
  }

  get inputProjectQPercentage(): number {
    return Number(this.targetForm.get('qpercentage').value);
  }

  get inputProjectType(): string {
    return this.selectedProjectType;
  }

  // ================================== getters and setters FINISH ======================================


  // ===================================== initialization START =========================================

  ngOnInit() {
    this.dropdownSettings = {
      singleSelection: false,
      defaultOpen: false,
      idField: 'legalEntityId',
      textField: 'legalEntityName',
      enableCheckAll: false,
      selectAllText: 'Select All',
      unSelectAllText: 'UnSelect All',
      allowSearchFilter: true
    };
    this.getLegalEntities();
    this.validateProjectEntityAssociation();
    this.isProjectTypeEmpty = true;
    
    this.existingProjectNumbers = this._projectsService.eiPrjTableDataFiltered
      .map(p => p.projectNumber?.toString().toUpperCase());
    this.existingProjectNames = this._projectsService.eiPrjTableDataFiltered
      .map(p => p.name?.toString().toUpperCase());
      
    this.clearForm();
    this._rightSideDrawerService.drawerOpen.subscribe(drawerData => {
      if (drawerData && drawerData.drawerTitle && drawerData.drawerTitle === "Add Project" && drawerData.data && drawerData.data.isTimeSurveyProject) {
        this.isProjAddingFromSurvey = drawerData.data.isTimeSurveyProject;
      }
    })
  }

  private getLegalEntities(): void {
    this.route.firstChild?.params.pipe(take(1), map(params => {
      const studyId = parseInt(params['studyId']);
      return { studyId }
    }), switchMap(({ studyId }) =>
      this.legalEntityService.getLegalEntities(studyId ? studyId : parseInt(this.engagementId))
      .pipe(map((response) => {
        this.legalEntities = response && response.result ? response.result
        .map(r => {
          return { ...r, legalEntityId: r.legalEntityId, legalEntityName: r.legalEntityName, isSelected: false }
        }).sort((a, b) => a.legalEntityName.localeCompare(b.legalEntityName)) : [];
        this.setSelectEntityAsociation();
      })))
    ).subscribe();
  }

  setSelectEntityAsociation(): void {
    if (this.legalEntities.length === 0) {
      this.projectEntityAssociationOptions.find(x => x.id === 1).isSelected = true;
      this.isProjectEntityAssociated = true;
    }
  }

  ngAfterViewInit() {
    this._subscriptions.push(
      this._projectsService.enableAddDrawer
        .pipe(delay(0))
        .subscribe(showAddDrawer => {
          if (showAddDrawer) {
            this.clearForm();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  private getProjectOptions(): CustomDropDown[] {
    const projectOptions = APP_CONSTANTS.PROJECT_OPTIONS;
    const options: CustomDropDown[] = [{ id: 0, value: '', isDisabled: false }];
    projectOptions.forEach((item, index) => {
      options.push({ id: index + 1, value: item, isDisabled: false });
    });
    return options;
  }

  private clearForm() {
    this.targetForm = this._formBuilder.group({
      qpercentage: [
        '100',
        [
          Validators.required,
          Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
          Validators.min(0),
          Validators.max(100),
        ],
      ],
      engagementId: [''],
      projectId: [''],
      projectNumber: [
        '',
        [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern('^[a-zA-Z0-9]+$'),
        ] /*Find out if number or string*/,
      ],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      desc: ['', [Validators.maxLength(500)]]
    });
  }

  // ===================================== initialization FINISH ========================================


  // ======================================= form events START ==========================================

  checkProjectNumber() {
    this.projectNoError = this.existingProjectNumbers.includes(this.inputProjectNumber.trim().toUpperCase());
  }
  checkProjectName() {
    this.projectNameDuplicateError = this.existingProjectNames.includes(this.inputProjectName.trim().toUpperCase());
  }

  onProjectNameChanged() {
    this.isProjectName = this.inputProjectName?.length > 0;
  }

  onProjectTypeSelected(optionSelected: CustomDropDown): void {
    if (optionSelected === null) {
      this.showProjectTypeValidation = this.selectedProjectType === '' ? true : false;
      this.customDropdownComponent.showError = this.selectedProjectType === '' ? true : false;
    } else {
      this.isProjectTypeEmpty = false;
      this.showProjectTypeValidation = false;
      this.customDropdownComponent.showError = false;
      this.selectedProjectType = optionSelected.value;
      this.onEditProjectData();
    }
  }

  onEditProjectData() {
    if (this.isInitialDataChanged()) {
      this._rightSideDrawerService.setMandatoryField(true, this.drawerTitle);
    } else {
      this._rightSideDrawerService.setMandatoryField(false, '');
    }
  }

  trimSpecialChar(ctrl: any) {
    this.targetForm.controls[ctrl].setValue(
      this.targetForm.get(ctrl).value.replace(/[^a-zA-Z0-9-()._#!$%^+=&,*@/ ]/g, ''),
    );
  }

  private isInitialDataChanged(): boolean {
    const initialDataChanged =
      this.inputProjectNumber !== '' ||
      this.inputProjectName != '' ||
      this.inputProjectType != '' ||
      this.inputProjectQPercentage != 100 ||
      this.inputProjectDescription != '';
    return initialDataChanged;
  }

  // ======================================= form events FINISH =========================================


  // ======================================= form buttons START =========================================

  getMappedEntities(): number[] {
    let mappedEnttities = this.legalEntities.filter(x => x.isSelected);
    let mappedEntityIds = mappedEnttities.map(x => x.legalEntityId);
    return mappedEntityIds;
  }

  addProjectButtonClick() {
    this.isAddProjectButtonDisabled = true;
    let project: Project = {
      engagementId: Number(this.engagementId),
      name: encodeText(this.inputProjectName),
      projectNumber: encodeText(this.inputProjectNumber),
      desc: encodeText(this.inputProjectDescription),
      projectType: this.selectedProjectType,
      qPercentage: this.inputProjectQPercentage,
      isValidated: this.isProjAddingFromSurvey,
      isAllEntityAssociated: this.projectEntityAssociationOptions.find(x => x.id === 1).isSelected ? true : false,
      legalEntities: this.getMappedEntities()
    };

    this._subscriptions.push(
      this._epAddProjectService.addProject(project).subscribe(
        (projectResponse: BaseResponse) => {
          if (projectResponse?.success) {
            this._toastrService.setSuccessMessage(
              this.calledFromEditSurvey
                ? Messages.Project.AddProjectFromTimeSurveyEdit
                : Messages.Project.AddProjectSuccess
            );
            this.projectCreatedSuccessfully(projectResponse.result as Project);
          } else {
            this.isAddProjectButtonDisabled = false;
            this._toastrService.setErrorMessage(Messages.Project.AddProjectFailure);
          }
        },
        error => {
          this.isAddProjectButtonDisabled = false;
          this._toastrService.setErrorMessage(Messages.Project.AddProjectAPIFailure);
        },
      )
    );
  }

  private projectCreatedSuccessfully(newProject: Project) {
    this._projectsService.eiPrjTableDataFiltered.unshift(newProject);
    if (newProject?.projectId && this.calledFromEditSurvey) {
      this.addNewProjectToSavedData(newProject?.projectId);
    }
    this._rightSideDrawerService.closeRightSideDrawer(
      this.calledFromEditSurvey
        ? ''
        : APP_CONSTANTS.IMPORT_PROJECTS_ADD_PROJECT
    );
  }

  private addNewProjectToSavedData(projectId: number) {
    const projectsActivitiesJson = this._sessionService.getSession(SESSION_CONST.SELECTED_PROJECTS_ACTIVITIES);
    if (projectsActivitiesJson?.length > 0) {
      const savedData = JSON.parse(projectsActivitiesJson) as SelectedDeletedProjectActivity[];
      savedData.push({
        activityType: ActivityTypeEnum.Project,
        id: projectId,
        selected: true
      } as SelectedDeletedProjectActivity);
      this._sessionService.setSession(
        SESSION_CONST.SELECTED_PROJECTS_ACTIVITIES,
        JSON.stringify(savedData)
      );
    }
  }

  cancelButtonClick() {
    this._rightSideDrawerService.closeRightSideDrawer();
  }

  onEntityAssociationChange(entityAssociationOption: { id: number, value: string, isSelected: boolean }): void {
    this.entityName = [];
    this.isSpecificEntitySelected = entityAssociationOption.id === 2 ? true : false;
    this.projectEntityAssociationOptions.forEach(option => {
      option.isSelected = option.id === entityAssociationOption.id ? true : false;
    });
    this.isProjectEntityAssociated = entityAssociationOption.id === 1 ? true : false;
  }

  onEntitySelect(selectedEntity: { legalEntityId: number, legalEntityName: string }): void {
    this.legalEntities.forEach(entity => {
      entity.isSelected = entity.legalEntityId === selectedEntity.legalEntityId ? true : entity.isSelected;
    });
    this.validateProjectEntityAssociation();
  }

  onEntityDeSelect(deSelectedEntity: { legalEntityId: number, legalEntityName: string }): void {
    this.legalEntities.forEach(entity => {
      entity.isSelected = entity.legalEntityId === deSelectedEntity.legalEntityId ? false : entity.isSelected;
    });
    this.validateProjectEntityAssociation();
  }

  validateProjectEntityAssociation(): void {
    this.isProjectEntityAssociated = this.isSpecificEntitySelected && this.legalEntities.some(x => x.isSelected) ? true : false;
  }

  isValidForm(): boolean {
    let isValid = (!this.targetForm.valid && this.isAddProjectButtonDisabled) || !this.isProjectName || this.projectNoError || this.projectNameDuplicateError || this.isProjectTypeEmpty || !this.isProjectEntityAssociated;
    return isValid;
  }
}
